import {useScheduleQuery, isOptimizedScheduleGuard} from '@domain/schedule'
import {AssetSnapshot} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Box, Collapse, Stack} from '@mui/material'
import {AssetLabel} from '@widgets/planning-comments'
import {
  PlanningChartGantt,
  PlanningChartLabels,
  PlanningChartLegend,
  PlanningChartScheduleDataProvider,
  ScheduleItemsContainer,
  TimeScaleFn,
  toPixel
} from '@widgets/planning-schedule-chart'
import moment, {Moment} from 'moment-timezone'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {usePersistingSettingState} from '../../hooks'
import {useSelectedOptimizerPlan} from '../../providers'
import {PlannerLSSettingsKeys} from '../../types'
import {getLocalizedDayMonthTimeFormat} from '../../utils'
import {CollapsibleCard} from '../CollapsibleCard'

import {TogglePlanTypeButton} from './TogglePlanTypeButton'

interface OptimizerPlanningChartCardProps {
  plantCode: string
  startOfPlan: Moment
  endOfPlan: Moment
  dateLabelHeight: number
  hourlyPriceHeight: number
  labelColumnWidth: number
  cellHeight: number
  cellWidth: number
  hoursDisplayed: number
  sortedAssets: AssetSnapshot[]
  xScale: TimeScaleFn
  hoursList: Moment[]
}

export const _OptimizerPlanningChartCard = React.forwardRef<
  HTMLDivElement,
  OptimizerPlanningChartCardProps
>(
  (
    {
      plantCode,
      startOfPlan,
      endOfPlan,
      dateLabelHeight,
      hourlyPriceHeight,
      labelColumnWidth,
      cellHeight,
      cellWidth,
      hoursDisplayed,
      xScale,
      hoursList,
      sortedAssets
    },
    ref
  ) => {
    const timezoneId = useUpmPlantTimezone()
    const {
      t,
      i18n: {language}
    } = useTranslation(FEATURE_NAMESPACE)

    const {visibleScheduleId} = useSelectedOptimizerPlan()

    const {data: schedule, isOptimized} = useScheduleQuery({
      plantId: plantCode,
      range: [startOfPlan, endOfPlan],
      scheduleId: visibleScheduleId
    })

    const scheduleLastUpdatedAt = isOptimizedScheduleGuard(schedule)
      ? schedule?.optimizationDetails.triggeredOn
      : schedule?.lastSaved.savedAt
    const scheduleLastUpdatedBy = isOptimizedScheduleGuard(schedule)
      ? schedule?.optimizationDetails.triggeredBy
      : schedule?.lastSaved.savedBy

    const optimizationRange = useMemo(() => {
      if (!isOptimized) {
        return schedule?.optimizationRange
      }
      const range = schedule?.optimizationDetails?.range
      if (!range) {
        return undefined
      }
      return {
        from: range.start,
        to: range.end
      }
    }, [schedule, isOptimized])

    const [{isCardOpen}, setIsPlanningCardOpen] = usePersistingSettingState(
      PlannerLSSettingsKeys.PlanningChartCardSettings,
      {isCardOpen: true}
    )
    const toggleCardOpen = () => setIsPlanningCardOpen({isCardOpen: !isCardOpen})

    return (
      <CollapsibleCard
        isCollapsed={!isCardOpen}
        onToggleCollapsed={toggleCardOpen}
        title={t('planning.planningSectionTitle')}
        subtitle={
          scheduleLastUpdatedAt &&
          scheduleLastUpdatedBy &&
          t('planning.lastSaved', {
            date: getLocalizedDayMonthTimeFormat(
              moment.utc(scheduleLastUpdatedAt),
              timezoneId,
              language
            ),
            name: scheduleLastUpdatedBy
          })
        }
        headerContent={
          <Stack direction="row" sx={{justifyContent: 'flex-end'}}>
            <TogglePlanTypeButton />
          </Stack>
        }
        {...dataTestId('optimizer_planning_chart_card')}
      >
        <Collapse in={isCardOpen}>
          <PlanningChartScheduleDataProvider scheduleId={visibleScheduleId}>
            <Stack
              sx={{position: 'relative', overflow: 'hidden'}}
              spacing={2}
              {...dataTestId('optimizer_planning_chart_card_content')}
            >
              <PlanningChartLegend
                shouldShowActualPricesLegend
                startOfPlan={startOfPlan}
                endOfPlan={endOfPlan}
              />
              <Stack direction="row">
                <Box sx={{mt: toPixel(2 * dateLabelHeight)}}>
                  <PlanningChartLabels
                    sortedAssets={sortedAssets}
                    hourlyPriceHeight={hourlyPriceHeight}
                    cellHeight={cellHeight}
                    width={labelColumnWidth}
                    shouldShowPriceInfo={true}
                    renderAssetLabel={(asset, labelWidth) => (
                      <AssetLabel
                        asset={asset}
                        width={labelWidth}
                        isReadOnly={true}
                        shouldShowComment={false}
                      />
                    )}
                  />
                </Box>
                <PlanningChartGantt
                  ref={ref}
                  startOfPlan={startOfPlan}
                  endOfPlan={endOfPlan}
                  numberOfAssets={sortedAssets.length}
                  dateLabelHeight={dateLabelHeight}
                  hourlyPriceHeight={hourlyPriceHeight}
                  cellHeight={cellHeight}
                  cellWidth={cellWidth}
                  optimizationRange={optimizationRange}
                  hoursDisplayed={hoursDisplayed}
                  xScale={xScale}
                  hoursList={hoursList}
                  shouldRoundCurrentTime
                  shouldShowExactPrices
                  shouldShowActualPricesIndicator
                  shouldShowColorCodedElectricityPrices
                  renderScheduleItems={(params) => (
                    <ScheduleItemsContainer
                      {...params}
                      shouldShowFixedForOptimizerInfo
                      shouldShowColorCodedElectricityPrices
                      shouldShowExactElectricityPrices
                      shouldShowStartEndTime={false}
                      shouldShowScheduleItemComments={false}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </PlanningChartScheduleDataProvider>
        </Collapse>
      </CollapsibleCard>
    )
  }
)
export const OptimizerPlanningChartCard = React.memo(_OptimizerPlanningChartCard)
OptimizerPlanningChartCard.displayName = 'OptimizerPlanningChartCard'
